import { useEffect, useRef } from 'react';
import {
  useSelectedKeywordMatch,
  useAllKeywordMatches,
} from '../transcriptionSlice';
import {
  TranscriptionStateToken,
  TranscriptionStateKeywordMatchedToken,
} from '../transcriptionSlice';
import { formatTimestamp } from './TranscriptViewer';
import {
  StyledMessage,
  StyledSpeaker,
  StyledTimestamp,
  StyledTokenContainer,
  StyledTokenHighlighted,
  StyledTokenRegular,
  TokenWithHighlightsWrapper,
} from './TranscriptViewer.styles';
import type {
  CompositeTranscriptionStateToken,
  TranscriptKeywordLocation,
} from './mergeTokens';
import { getKeywordLocation, tokenContainsTokenId } from './mergeTokens';

const formatSpeakerName = (
  token: TranscriptionStateToken | CompositeTranscriptionStateToken,
  showLanguageCodes = false,
): string => {
  const baseName = token?.speaker_display_name || '';
  const speakerName = token?.speaker_name?.toLowerCase();

  // Only add language code for caller (not dispatcher) when feature is enabled
  if (
    showLanguageCodes &&
    speakerName === 'caller' &&
    token?.detected_language
  ) {
    const langCode = token.detected_language.toUpperCase();
    return `${baseName} (${langCode})`;
  }

  return baseName;
};

interface Props {
  token: TranscriptionStateToken | CompositeTranscriptionStateToken;
  selectedTranscript: 'original' | 'translated';
  showLanguageCodes?: boolean;
}

const TranscriptTokenWithHighlighting = ({
  token,
  selectedTranscript,
  showLanguageCodes = false,
}: Props) => {
  const selectedKeywordMatch = useSelectedKeywordMatch();
  const highlightKeywordSets = useAllKeywordMatches();

  const highlightMatches = highlightKeywordSets
    // get a flattened array of all matches
    .flatMap(keywordSet => {
      return (
        keywordSet.matches
          // only keep matches that match this token
          .filter(match => tokenContainsTokenId(token, match.token_id))
      );
    });

  // keep track of the selected match index, so we can scroll to it:
  const messageRef = useRef<HTMLDivElement>(null);
  const selectedMatchIndex = selectedKeywordMatch?.matchIndex;

  useEffect(() => {
    if (messageRef.current) {
      if (selectedMatchIndex === undefined) {
        // there is no selected keyword at all, ignore:
        return;
      }
      if (!selectedKeywordMatch) {
        // there is no selected keyword set, ignore:
        return;
      }
      const match = selectedKeywordMatch.keywordSet.matches[selectedMatchIndex];
      if (!tokenContainsTokenId(token, match.token_id)) {
        // this token is not the selected one, ignore:
        return;
      }
      // this token is the selected one, scroll to it:
      messageRef.current.scrollIntoView({
        // NOTE: behavior: 'smooth' will cause chrome to not scroll if we set it
        //   and accessibility features are enabled (which are often enabled by
        //   default). See https://stackoverflow.com/a/62483914/51061
        block: 'start',
        inline: 'nearest',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatchIndex]);

  return (
    <StyledMessage
      data-name="transcript-token-message-container"
      ref={messageRef}
    >
      <StyledSpeaker
        speakerName={token.speaker_name}
        data-name="transcript-token-speaker"
      >
        {formatSpeakerName(token, showLanguageCodes)}
      </StyledSpeaker>{' '}
      {token?.start_time && (
        <StyledTimestamp data-name="styled-timestamp">
          {formatTimestamp(token?.start_time)}
        </StyledTimestamp>
      )}
      <SpansWithHighlights
        token={token}
        matches={highlightMatches}
        selectedTranscript={selectedTranscript}
      />
    </StyledMessage>
  );
};

function SpansWithHighlights({
  token,
  matches,
  selectedTranscript,
}: {
  token: TranscriptionStateToken | CompositeTranscriptionStateToken;
  matches: TranscriptionStateKeywordMatchedToken[];
  selectedTranscript: 'original' | 'translated';
}): JSX.Element {
  // Sort matches so we can begin at the start of the string:
  const sortedMatches = [...matches].sort(
    (a, b) => a.match_index - b.match_index,
  );
  // get the transcript text to render:
  const transcript =
    selectedTranscript === 'translated' && token.translated
      ? token.translated
      : token?.transcript || '';

  const matchFilter = (
    value: TranscriptKeywordLocation | null,
  ): value is TranscriptKeywordLocation => {
    return value !== null;
  };

  return (
    <StyledTokenContainer data-name="styled-token-container">
      <StyledTokenRegular key="opening-quote">"</StyledTokenRegular>
      {sortedMatches.length > 0 ? (
        sortedMatches
          .map(match => getKeywordLocation(token, match))
          // getKeywordLocation returns null if the match is not in this token
          .filter(matchFilter)
          .map((match, matchIndex, matches) => {
            // track the index where last span left off
            const lastSpanCharacterIndex =
              matchIndex > 0
                ? matches[matchIndex - 1].match_index +
                  matches[matchIndex - 1].match_length
                : 0;

            return (
              <TokenWithHighlightsWrapper key={match.match_index}>
                {
                  // text before the highlight
                  match.match_index > lastSpanCharacterIndex && (
                    <StyledTokenRegular
                      data-name="transcript-token-regular"
                      key={`before-${matchIndex}`}
                    >
                      {transcript.substring(
                        lastSpanCharacterIndex,
                        match.match_index,
                      )}
                    </StyledTokenRegular>
                  )
                }

                {/* highlighted span for the match */}
                <StyledTokenHighlighted
                  data-name="styled-token-highlighted"
                  key={`highlight-${matchIndex}`}
                >
                  {transcript.substring(
                    match.match_index,
                    match.match_index + match.match_length,
                  )}
                </StyledTokenHighlighted>

                {
                  // add any remaining text after the last highlight
                  matchIndex == matches.length - 1 &&
                    match.match_index + match.match_length <
                      transcript.length && (
                      <StyledTokenRegular key={`remaining-${matchIndex}`}>
                        {transcript.substring(
                          match.match_index + match.match_length,
                        )}
                      </StyledTokenRegular>
                    )
                }
              </TokenWithHighlightsWrapper>
            );
          })
      ) : (
        <StyledTokenRegular
          key="no-highlights-span"
          data-name="transcript-token-message"
        >
          {transcript}
        </StyledTokenRegular>
      )}
      <StyledTokenRegular key="closing-quote">"</StyledTokenRegular>
    </StyledTokenContainer>
  );
}

export default TranscriptTokenWithHighlighting;
